import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeftRight, Bell, ChevronDown, Plus, Star, StarOff,
  TrendingDown, TrendingUp, Wallet,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import DataSourceBadge from "../DataSourceBadge";

/**
 * Header strip for the Asset Detail page: name, big price, change pill,
 * action buttons (Paper trade / Alert / Compare) and the watchlist dropdown.
 *
 * Pure presentational — receives all data + handlers from the parent page.
 */
export default function AssetHeader({
  data, type, range, user,
  lists, watchIds, watchLoading,
  onOpenTrade, onOpenAlert, onToggleList,
}) {
  const isUp = (data?.change_pct ?? 0) >= 0;
  const trendColor = isUp ? "#5C8D73" : "#C66B59";
  const anyWatched = Object.keys(watchIds).length > 0;
  const assetTypeLabel =
    type === "stock" ? "Stock" :
    type === "etf" ? "ETF" :
    type === "forex" ? "Forex" : "Cryptocurrency";

  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 animate-fade-up">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#6B7068]">
            {assetTypeLabel}
          </span>
          <span className="w-1 h-1 rounded-full bg-[#6B7068]" />
          <span className="font-mono-ticker text-xs text-[#6B7068]">{data.symbol}</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-medium text-[#2A2F2B]">
          {data.name}
        </h1>
        <div className="flex items-baseline gap-3 mt-3">
          <div className="font-mono-ticker text-4xl sm:text-5xl text-[#2A2F2B]" data-testid="asset-price">
            ${data.price < 1
              ? data.price.toFixed(4)
              : data.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div
            className="flex items-center gap-1 px-2.5 py-1 rounded-full text-sm font-mono-ticker font-semibold"
            style={{ backgroundColor: `${trendColor}18`, color: trendColor }}
            data-testid="asset-change"
          >
            {isUp ? <TrendingUp className="w-3.5 h-3.5" strokeWidth={2.5} /> : <TrendingDown className="w-3.5 h-3.5" strokeWidth={2.5} />}
            {isUp ? "+" : ""}{data.change_pct.toFixed(2)}%
            <span className="text-[#6B7068] font-normal ml-1">{range}</span>
          </div>
        </div>
        <DataSourceBadge source={data.source} stale={data.stale} className="mt-3" />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          onClick={onOpenTrade}
          data-testid="asset-action-paper"
          className="rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white"
        >
          <Wallet className="w-4 h-4 mr-2" strokeWidth={1.5} /> Paper trade
        </Button>
        {user && (
          <Button
            onClick={onOpenAlert}
            data-testid="asset-action-alert"
            variant="outline"
            className="rounded-full bg-white border-[#EAE6E1] hover:border-[#D18E63] text-[#2A2F2B]"
          >
            <Bell className="w-4 h-4 mr-2" strokeWidth={1.5} /> Set alert
          </Button>
        )}
        <Button
          asChild
          variant="outline"
          data-testid="asset-action-compare"
          className="rounded-full bg-white border-[#EAE6E1] hover:border-[#547A8F] text-[#2A2F2B]"
        >
          <Link to={`/compare?a=${data.symbol}&a_type=${type}`}>
            <ArrowLeftRight className="w-4 h-4 mr-2" strokeWidth={1.5} /> Compare
          </Link>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              data-testid="asset-action-watchlist"
              className={`rounded-full transition-soft ${
                anyWatched
                  ? "bg-[#D18E63] hover:bg-[#b87752] text-white"
                  : "bg-white border border-[#EAE6E1] hover:border-[#547A8F] text-[#2A2F2B]"
              }`}
            >
              {anyWatched ? (
                <><Star className="w-4 h-4 mr-2 fill-current" strokeWidth={1.5} /> In watchlist</>
              ) : (
                <><StarOff className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add to list</>
              )}
              <ChevronDown className="w-4 h-4 ml-1" strokeWidth={1.5} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-xl w-56" data-testid="watchlist-dropdown">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#6B7068]">
              Your lists
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {lists.length === 0 && (
              <DropdownMenuItem disabled>No lists yet</DropdownMenuItem>
            )}
            {lists.map((l) => (
              <DropdownMenuItem
                key={l.id}
                onClick={() => onToggleList(l.id)}
                data-testid={`dropdown-list-${l.id}`}
                className="rounded-lg cursor-pointer"
                disabled={watchLoading}
              >
                <div className="flex items-center justify-between w-full">
                  <span>{l.name}</span>
                  {watchIds[l.id] && <Star className="w-4 h-4 text-[#D18E63] fill-[#D18E63]" strokeWidth={1.5} />}
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/watchlist" className="cursor-pointer">
                <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Manage lists
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
